/**
 * Image preloading utility for app icons
 * Preloads images in background to improve perceived performance
 */

import { RANKINGS_API_BASE_URL } from "../api/rankingsApi";

const preloadedImages = new Set<string>();

/**
 * Convert relative paths to absolute URLs
 * API returns relative paths like /app-rankings/icons/abc.png
 */
function toAbsoluteUrl(url: string): string {
  return url.startsWith("http") ? url : `${RANKINGS_API_BASE_URL}${url}`;
}

/**
 * Preload a single image URL
 * Returns a promise that resolves when image loads or rejects on error
 */
export function preloadImage(url: string): Promise<void> {
  const absoluteUrl = toAbsoluteUrl(url);

  return new Promise((resolve, reject) => {
    // Skip if already preloaded
    if (preloadedImages.has(absoluteUrl)) {
      resolve();
      return;
    }

    const img = new Image();
    img.referrerPolicy = "no-referrer";

    // Set timeout to prevent hanging on unreachable URLs
    const timeout = setTimeout(() => {
      reject(new Error(`Timeout preloading image: ${absoluteUrl}`));
    }, 5000);

    img.onload = () => {
      clearTimeout(timeout);
      preloadedImages.add(absoluteUrl);
      resolve();
    };

    img.onerror = () => {
      clearTimeout(timeout);
      // Don't add failed images to preloaded set - allow them to retry
      reject(new Error(`Failed to preload image: ${absoluteUrl}`));
    };

    img.src = absoluteUrl;
  });
}

/**
 * Preload multiple image URLs
 * Starts all preloads in parallel but resolves once all complete (or fail)
 */
export async function preloadImages(urls: string[]): Promise<void> {
  // Convert to absolute URLs and filter out duplicates and already-preloaded images
  const urlsToPreload = urls
    .map(toAbsoluteUrl)
    .filter(
      (url, index, self) =>
        self.indexOf(url) === index && !preloadedImages.has(url)
    );

  if (urlsToPreload.length === 0) {
    return;
  }

  // Start all preloads in parallel - we don't fail if individual images fail
  // This allows the UI to render while retrying failed images on-demand
  await Promise.allSettled(urlsToPreload.map(preloadImage));
}

/**
 * Clear the preload cache
 * Useful when switching between different datasets (store/country changes)
 */
export function clearPreloadCache(): void {
  preloadedImages.clear();
}

/**
 * Check if an image is already preloaded
 */
export function isImagePreloaded(url: string): boolean {
  return preloadedImages.has(toAbsoluteUrl(url));
}
